import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { StarPicker, StarRating, scoreBand } from "@/components/site/Stars";
import AlumniAutocomplete from "@/components/site/AlumniAutocomplete";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronRight, ChevronLeft, Send, Check, ThumbsUp, ThumbsDown, Star, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { saveCurrentAsReturnTo, saveReturnTo } from "@/lib/authRedirect";
import { track } from "@/lib/analytics";

/**
 * Compose the current school-detail URL WITH `?review=1` so the review-open
 * intent survives an external OAuth redirect. `saveCurrentAsReturnTo` alone
 * cannot capture that intent because the wizard is UI-state on top of the
 * page — the URL by itself doesn't remember it.
 */
function _saveReviewReturnTo() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("review", "1");
    saveReturnTo(url.pathname + url.search + url.hash, window.scrollY || 0);
  } catch {
    // Fallback if URL API misbehaves (very old browsers)
    saveCurrentAsReturnTo();
  }
}

// v1.1 §1A — FINAL rating keys (Indonesian). Order matches the spec doc.
// `hideForStudent` on "value" preserves the pre-existing rule that active
// students don't rate the biaya dimension.
export const RATING_KEYS = [
  { key: "fasilitas",  label: "Sarana & Fasilitas" },
  { key: "keamanan",   label: "Keamanan & Kesejahteraan Siswa" },
  { key: "kurikulum",  label: "Kurikulum & Pembelajaran" },
  { key: "value",      label: "Kesesuaian Value / Biaya", hideForStudent: true },
  { key: "minat",      label: "Pengembangan Minat & Potensi" },
  { key: "komunikasi", label: "Komunikasi Sekolah–Orang Tua" },
];

/** Spec v1.1 §1A — FINAL question copy per dimension, identical for every persona.
 * The old parent/kid variants are gone: the spec is emphatic that names AND
 * questions are FINAL and must not be tweaked. */
function getHelper(key /* role param kept for signature compat but unused */) {
  return {
    fasilitas:  "Apakah gedung, ruang kelas, laboratorium, lapangan, perpustakaan, dan fasilitas lainnya terawat serta mendukung kegiatan belajar?",
    keamanan:   "Seberapa baik sekolah menyediakan lingkungan yang aman bagi siswa, termasuk dalam menangani keamanan, perundungan, dan kesejahteraan siswa?",
    kurikulum:  "Apakah kurikulum dan proses pembelajaran memberikan materi serta tingkat tantangan yang sesuai bagi siswa?",
    value:      "Seberapa sepadan biaya sekolah dengan kualitas pendidikan, layanan, dan fasilitas yang diterima?",
    minat:      "Seberapa baik sekolah menyediakan kesempatan dan dukungan bagi siswa untuk mengembangkan minat dan potensi di luar akademik?",
    komunikasi: "Seberapa baik sekolah memberikan informasi mengenai perkembangan anak serta berkomunikasi secara terbuka dengan orang tua?",
  }[key];
}

/** Rating keys applicable to a given reviewer role (excludes value_for_money for students). */
export function keysForRole(role) {
  return RATING_KEYS.filter((k) => !(role === "student" && k.hideForStudent));
}

// Spec v1.1 §2 — initial value is 0 ("belum dipilih") so every star renders
// grey until the reviewer taps. This makes the sticky-footer `canSubmit` gate
// meaningful: nothing pre-checked can be accidentally submitted.
const emptyRatings = () => Object.fromEntries(RATING_KEYS.map(({ key }) => [key, 0]));

/**
 * Spec v1.1 §2 — Single-page long-form composer (was a 3-step wizard).
 * All blocks (Penilaian · Cerita · Kirim) render on one scrollable page; a
 * sticky footer shows the live headline score + the primary Kirim button.
 * Props: schoolId, schoolName, onCancel, onSuccess (called with schoolId+name after submit)
 */
export default function ReviewWizard({ schoolId, schoolName, schoolLevels = [], onCancel, onSuccess }) {
  const nav = useNavigate();
  const { user, checkAuth } = useAuth();
  // Iter154 — session-expired handling. Reviewer keeps their words even if
  // the JWT dies mid-form; a lightweight modal offers a re-login path.
  const [sessionExpired, setSessionExpired] = useState(false);
  const [ratings, setRatings] = useState(emptyRatings());
  // Spec v1.1 §1B — kebahagiaan (emoji 1-4) is OPSIONAL and does NOT feed the score.
  const [kebahagiaan, setKebahagiaan] = useState(null);
  // Spec v1.1 §1C — rekomendasi (0-10) is OPSIONAL and does NOT feed the score.
  // Renamed from the legacy `nps` state var; the slider defaults to unset (null) not 9.
  const [rekomendasi, setRekomendasi] = useState(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [pros, setPros] = useState("");
  const [cons, setCons] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [reviewerRole, setReviewerRole] = useState("parent"); // parent | student | alumni
  // Iter140 — Periode bersekolah dua-dropdown. `yearStart` & `yearEnd` menggantikan
  // `attendedYear` (parent/student) dan `gradYear` (alumni) sebagai satu API.
  // `yearEnd === "current"` = "Masih bersekolah (sekarang)" (parent/student only).
  const [yearStart, setYearStart] = useState("");
  const [yearEnd, setYearEnd] = useState("");
  const [alumniNextStudy, setAlumniNextStudy] = useState("");
  const [alumniProfession, setAlumniProfession] = useState("");
  const [alumniHighestEdu, setAlumniHighestEdu] = useState(""); // D3 | S1 | S2 | S3
  const [alumniEduD3, setAlumniEduD3] = useState("");
  const [alumniEduS1, setAlumniEduS1] = useState("");
  const [alumniEduS2, setAlumniEduS2] = useState("");
  const [alumniEduS3, setAlumniEduS3] = useState("");
  const [alumniCurrentCompany, setAlumniCurrentCompany] = useState("");
  const [alumniPreviousCompany, setAlumniPreviousCompany] = useState("");
  // Jenjang yang direview — hanya wajib jika sekolah menawarkan >1 jenjang (TK/SD/SMP/SMA)
  const availableLevels = (schoolLevels || []).map((l) => (l || "").toUpperCase()).filter((l) => ["TK","SD","SMP","SMA"].includes(l));
  const uniqueLevels = Array.from(new Set(availableLevels));
  const multiLevel = uniqueLevels.length > 1;
  const [educationLevelReviewed, setEducationLevelReviewed] = useState(multiLevel ? "" : (uniqueLevels[0] || ""));
  // Spec v1.1 §2 — 4-point Persetujuan (all required to submit).
  const [consent1, setConsent1] = useState(false); // pengalaman pribadi
  const [consent2, setConsent2] = useState(false); // tanpa imbalan / tekanan
  const [consent3, setConsent3] = useState(false); // tanpa identitas anak
  const [consent4, setConsent4] = useState(false); // setuju Pedoman Komunitas
  const allConsentsChecked = consent1 && consent2 && consent3 && consent4;
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Escalating processing state so the submitter never thinks the page froze.
  // 0 → hidden, 1 → "Sedang mengirim…", 2 → "Masih memproses… jangan refresh".
  // Bumps to level 2 after 5 s so long uploads (photos, slow network) still
  // reassure the user. Cleared on completion regardless of outcome.
  const [submitStage, setSubmitStage] = useState(0);
  useEffect(() => {
    if (!busy) { setSubmitStage(0); return undefined; }
    setSubmitStage(1);
    const t = setTimeout(() => setSubmitStage(2), 5000);
    return () => clearTimeout(t);
  }, [busy]);
  // Iter141 — surface inline field errors only after the reviewer taps Submit once
  // (avoids scaring people with red states before they've had a chance to fill).
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // Iter142 — Draft autosave. Every 5s snapshot the form to localStorage so the
  // reviewer never loses their words if the modal is closed by accident. Consents
  // are intentionally skipped: freshly ticked each session.
  const DRAFT_KEY = `k12_review_draft_${schoolId || "unknown"}`;
  const [draftSavedAt, setDraftSavedAt] = useState(null);

  // Restore-once on mount. Guarded by `didRestoreRef` because
  // `educationLevelReviewed` may auto-init to a single level and we don't
  // want the restore to clobber that after the user has already typed.
  const didRestoreRef = useRef(false);
  useEffect(() => {
    if (didRestoreRef.current || !schoolId) return;
    didRestoreRef.current = true;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d.reviewerRole) setReviewerRole(d.reviewerRole);
      if (d.yearStart) setYearStart(d.yearStart);
      if (d.yearEnd) setYearEnd(d.yearEnd);
      if (d.educationLevelReviewed) setEducationLevelReviewed(d.educationLevelReviewed);
      if (d.ratings && typeof d.ratings === "object") setRatings((prev) => ({ ...prev, ...d.ratings }));
      if (d.kebahagiaan !== undefined) setKebahagiaan(d.kebahagiaan);
      if (d.rekomendasi !== undefined) setRekomendasi(d.rekomendasi);
      if (typeof d.title === "string") setTitle(d.title);
      if (typeof d.body === "string") setBody(d.body);
      if (typeof d.pros === "string") setPros(d.pros);
      if (typeof d.cons === "string") setCons(d.cons);
      if (typeof d.isAnonymous === "boolean") setIsAnonymous(d.isAnonymous);
      if (typeof d.alumniNextStudy === "string") setAlumniNextStudy(d.alumniNextStudy);
      if (typeof d.alumniProfession === "string") setAlumniProfession(d.alumniProfession);
      if (typeof d.alumniHighestEdu === "string") setAlumniHighestEdu(d.alumniHighestEdu);
      if (typeof d.alumniEduD3 === "string") setAlumniEduD3(d.alumniEduD3);
      if (typeof d.alumniEduS1 === "string") setAlumniEduS1(d.alumniEduS1);
      if (typeof d.alumniEduS2 === "string") setAlumniEduS2(d.alumniEduS2);
      if (typeof d.alumniEduS3 === "string") setAlumniEduS3(d.alumniEduS3);
      if (typeof d.alumniCurrentCompany === "string") setAlumniCurrentCompany(d.alumniCurrentCompany);
      if (typeof d.alumniPreviousCompany === "string") setAlumniPreviousCompany(d.alumniPreviousCompany);
      // Consents are normally re-ticked each session, but a draft with
      // `pendingSubmit:true` was persisted right after all 4 were checked —
      // restoring them lets the reviewer press Submit once on return.
      if (d.consent1) setConsent1(true);
      if (d.consent2) setConsent2(true);
      if (d.consent3) setConsent3(true);
      if (d.consent4) setConsent4(true);
      if (d.savedAt) setDraftSavedAt(d.savedAt);
    } catch { /* corrupted draft — ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Snapshot every 5s. Uses refs implicitly (React reads latest state each tick
  // because the interval callback closes over the current render's setters/vars
  // via a fresh reference each render — we intentionally re-arm on every state
  // change by keying `useEffect` on the payload signature would blow the timer,
  // so instead we read the latest state through the closure by capturing values
  // in a `latestRef`).
  const latestDraftRef = useRef({});
  latestDraftRef.current = {
    schoolId,
    reviewerRole, yearStart, yearEnd, educationLevelReviewed,
    ratings, kebahagiaan, rekomendasi,
    title, body, pros, cons, isAnonymous,
    alumniNextStudy, alumniProfession, alumniHighestEdu,
    alumniEduD3, alumniEduS1, alumniEduS2, alumniEduS3,
    alumniCurrentCompany, alumniPreviousCompany,
  };
  useEffect(() => {
    if (!schoolId) return undefined;
    const id = setInterval(() => {
      const snap = latestDraftRef.current;
      // Skip if the entire form is still blank (avoids polluting storage
      // with an empty draft the moment the wizard opens).
      const isBlank = !snap.body && !snap.yearStart && !snap.yearEnd &&
        Object.values(snap.ratings || {}).every((v) => !v) &&
        snap.kebahagiaan == null && snap.rekomendasi == null;
      if (isBlank) return;
      try {
        const savedAt = new Date().toISOString();
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...snap, savedAt }));
        setDraftSavedAt(savedAt);
      } catch { /* quota/private-mode — noop */ }
    }, 5000);
    return () => clearInterval(id);
  }, [schoolId, DRAFT_KEY]);

  // Human-friendly relative time for the "Draft tersimpan X lalu" hint.
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setNowTick((n) => n + 1), 15000);
    return () => clearInterval(t);
  }, []);
  const draftAgeLabel = (() => {
    if (!draftSavedAt) return "";
    // read `nowTick` so this recomputes as the interval ticks
    void nowTick;
    const secs = Math.max(0, Math.floor((Date.now() - new Date(draftSavedAt).getTime()) / 1000));
    if (secs < 15) return "baru saja";
    if (secs < 60) return `${secs} detik lalu`;
    const m = Math.floor(secs / 60);
    if (m < 60) return `${m} menit lalu`;
    const h = Math.floor(m / 60);
    return `${h} jam lalu`;
  })();

  // Scroll targets — first invalid field gets `scrollIntoView` on failed submit.
  const periodRef = useRef(null);
  const levelRef = useRef(null);
  const ratingRefs = useRef({});
  const kebahagiaanRef = useRef(null);
  const rekomendasiRef = useRef(null);
  const bodyRef = useRef(null);
  const consentsRef = useRef(null);

  // Freeze bug fix (Feb 2026): if the outer review modal is closed while this
  // wizard's nested shadcn/Radix <Dialog> (Pedoman Komunitas) is still open or
  // animating, Radix's cleanup is skipped — <body> can end up with residual
  // `pointer-events: none` and the `data-scroll-locked` attribute, which
  // freezes the entire page. On unmount we forcibly scrub those side-effects.
  useEffect(() => {
    return () => {
      document.body.style.pointerEvents = "";
      document.body.removeAttribute("data-scroll-locked");
    };
  }, []);

  // ESC key closes the Pedoman Komunitas overlay when open (see the inline
  // overlay near the bottom of the return). Registered only while open so we
  // don't intercept keys the user needs elsewhere.
  useEffect(() => {
    if (!guidelinesOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setGuidelinesOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [guidelinesOpen]);

  // Iter140 — Periode dropdown helpers.
  const currentYear = new Date().getFullYear();
  const MIN_YEAR = 2000;
  // Alumni tidak boleh "masih sekolah"; parent+student boleh.
  const allowCurrentEnd = reviewerRole !== "alumni";
  // Reset `yearEnd` bila `yearStart` melewati nilai `yearEnd` yang sudah dipilih.
  useEffect(() => {
    if (!yearEnd || yearEnd === "current") return;
    if (yearStart && parseInt(yearEnd, 10) < parseInt(yearStart, 10)) {
      setYearEnd("");
    }
  }, [yearStart, yearEnd]);
  // Alumni tidak pernah "current"; reset kalau role beralih ke alumni.
  useEffect(() => {
    if (reviewerRole === "alumni" && yearEnd === "current") setYearEnd("");
  }, [reviewerRole, yearEnd]);
  const rolePrefix = reviewerRole === "parent" ? "Wali Murid" : reviewerRole === "student" ? "Siswa" : "Alumni";
  const periodePreview = (() => {
    if (!yearStart) return "";
    if (!yearEnd) return "";
    if (yearEnd === "current") return `${rolePrefix} Angkatan ${yearStart}–sekarang`;
    return yearStart === yearEnd
      ? `${rolePrefix} Angkatan ${yearStart}`
      : `${rolePrefix} Angkatan ${yearStart}–${yearEnd}`;
  })();
  // Canonical string yang dikirim ke backend (parent/student) — cocok dengan validator.
  const attendedRangeString = (() => {
    if (!yearStart || !yearEnd) return "";
    if (yearEnd === "current") return `${yearStart}–sekarang`;
    return yearStart === yearEnd ? `${yearStart}` : `${yearStart}–${yearEnd}`;
  })();

  const periodValid = reviewerRole === "alumni"
    ? Boolean(yearStart && yearEnd && yearEnd !== "current")
    : Boolean(yearStart && yearEnd);
  const levelValid = !multiLevel || Boolean(educationLevelReviewed);
  const identityValid = periodValid && levelValid;
  // Spec v1.1 §2 — story minimum length. User chose 20 chars for "Ceritakan pengalamanmu".
  const MAIN_STORY_MIN = 20;
  const bodyValid = body.trim().length >= MAIN_STORY_MIN;
  const submit = async () => {
    // Guardrails (matches sticky-footer disabled state, but re-check server-side too).
    if (!identityValid) {
      toast.error(reviewerRole === "alumni"
        ? "Mohon lengkapi tahun masuk & tahun lulus."
        : "Mohon lengkapi periode bersekolah.");
      return;
    }
    if (!bodyValid) {
      toast.error(`Cerita utama minimal ${MAIN_STORY_MIN} karakter.`);
      return;
    }
    if (!allConsentsChecked) {
      toast.error("Mohon centang semua persetujuan sebelum mengirim.");
      return;
    }
    // Guest guard (Feb 2026): capture draft + intended return URL BEFORE the
    // API call so a 401 never wipes the reviewer's work. After OAuth the
    // ReviewWizard reopens with all fields (incl. consents) hydrated and a
    // subtle toast prompts the user to double-check before pressing Submit.
    // (Auto-submit was removed — see the resume-toast useEffect below.)
    if (!user) {
      try {
        const snap = latestDraftRef.current;
        localStorage.setItem(DRAFT_KEY, JSON.stringify({
          ...snap,
          // Persist consents together with pendingSubmit so the reviewer can
          // hit Submit again in one tap after auth. All 4 MUST have been
          // ticked to have reached this line in submit().
          consent1: true, consent2: true, consent3: true, consent4: true,
          savedAt: new Date().toISOString(), pendingSubmit: true,
        }));
      } catch { /* quota/private-mode — noop */ }
      toast.info("Masuk untuk mengirim review. Draft Anda tersimpan otomatis.");
      // Save return URL with `?review=1` so the wizard auto-reopens after OAuth.
      _saveReviewReturnTo();
      nav("/login");
      return;
    }
    setBusy(true);
    try {
      const appliedKeys = keysForRole(reviewerRole).map((k) => k.key);
      const filteredRatings = Object.fromEntries(Object.entries(ratings).filter(([k]) => appliedKeys.includes(k)));
      const payload = {
        school_id: schoolId,
        ratings: filteredRatings,
        // Spec v1.1 §1B/§1C — kebahagiaan & rekomendasi are TOP-LEVEL, not inside `ratings`.
        kebahagiaan: kebahagiaan ?? null,
        rekomendasi: rekomendasi ?? null,
        body,
        // Spec v1.1 §2 — pros/cons remain OPSIONAL. Backend expects strings, so pass empty on skip.
        pros: pros.trim(),
        cons: cons.trim(),
        is_anonymous: isAnonymous,
        reviewer_role: reviewerRole,
      };
      if (reviewerRole === "alumni") {
        payload.graduation_year = parseInt(yearEnd, 10);
        payload.attended_start_year = parseInt(yearStart, 10);
        payload.alumni_next_study = alumniNextStudy.trim();
        payload.alumni_current_profession = alumniProfession.trim();
        payload.alumni_highest_edu = alumniHighestEdu || null;
        payload.alumni_edu_d3 = alumniEduD3.trim();
        payload.alumni_edu_s1 = alumniEduS1.trim();
        payload.alumni_edu_s2 = alumniEduS2.trim();
        payload.alumni_edu_s3 = alumniEduS3.trim();
        payload.alumni_current_company = alumniCurrentCompany.trim();
        payload.alumni_previous_company = alumniPreviousCompany.trim();
      } else if (reviewerRole === "parent" || reviewerRole === "student") {
        payload.attended_academic_year = attendedRangeString;
      }
      if (educationLevelReviewed) {
        payload.education_level_reviewed = educationLevelReviewed;
      }
      // Iter154 — pre-flight auth check. Silent /auth/me sync catches an
      // already-expired token before we send the review payload, so the
      // reviewer sees the friendly re-login modal instead of a raw 401 toast.
      try {
        await checkAuth();
      } catch { /* checkAuth swallows non-401 errors; keep going */ }

      try {
        await api.post("/reviews", payload);
        // submit_review fires ONLY after the backend confirms 200.
        // De-dup by school_id in analytics.js prevents the retry path from
        // double-firing. No review text is ever sent — only public IDs.
        track("submit_review", { school_id: schoolId });
      } catch (err) {
        if (err?.response?.status === 401) {
          // Force-persist the current draft synchronously (autosave may not
          // have flushed the last 5 s of edits) and flag it for auto-resume.
          try {
            const snap = latestDraftRef.current;
            localStorage.setItem(DRAFT_KEY, JSON.stringify({
              ...snap,
              // See guest-guard rationale above — consents were true at submit.
              consent1: true, consent2: true, consent3: true, consent4: true,
              savedAt: new Date().toISOString(), pendingSubmit: true,
            }));
          } catch { /* noop */ }
          setSessionExpired(true);
          return; // don't propagate — modal takes over
        }
        throw err;
      }
      // Successful submit → wipe autosaved draft.
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ }
      if (onSuccess) onSuccess({ schoolId, schoolName });
      else nav(`/review-success?school_id=${encodeURIComponent(schoolId)}&school_name=${encodeURIComponent(schoolName || "")}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengirim review");
    } finally { setBusy(false); }
  };

  // After re-auth, if the persisted draft was flagged `pendingSubmit:true`
  // by the guest-guard branch of submit(), the wizard reopens with every
  // field (including consents) hydrated by the mount restore effect.
  //
  // Per Feb 2026 UX decision, we no longer auto-submit — auto-submit felt
  // jumpy. Instead we:
  //   1. Clear the `pendingSubmit` flag from localStorage so refresh doesn't
  //      re-trigger the toast, and hard refresh mid-review behaves normally.
  //   2. Show a subtle info toast asking the reviewer to double-check before
  //      pressing Submit.
  // The draft itself is preserved and only cleared after a successful manual
  // POST /reviews.
  const didSurfaceResumeToastRef = useRef(false);
  useEffect(() => {
    if (didSurfaceResumeToastRef.current || !user) return;
    let raw;
    try { raw = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"); } catch { raw = null; }
    if (raw && raw.pendingSubmit && raw.schoolId === schoolId) {
      didSurfaceResumeToastRef.current = true;
      try {
        const { pendingSubmit, _resumed, ...rest } = raw;
        localStorage.setItem(DRAFT_KEY, JSON.stringify(rest));
      } catch { /* noop */ }
      toast.info("Review Anda sudah tersimpan. Silakan periksa kembali sebelum dikirim.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const avgOverall = useMemo(() => {
    const appliedKeys = keysForRole(reviewerRole).map((k) => k.key);
    const vals = appliedKeys.map((k) => ratings[k]).filter((v) => typeof v === "number");
    return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
  }, [ratings, reviewerRole]);

  // Spec v1.1 §1D — headline label based on the arithmetic mean of the 6 kualitas dimensions.
  const scoreLabelText = (() => {
    if (avgOverall >= 4.5) return "Sangat Baik";
    if (avgOverall >= 3.5) return "Bagus";
    if (avgOverall >= 2.5) return "Cukup";
    if (avgOverall >= 1.5) return "Kurang";
    if (avgOverall > 0)     return "Perlu Perhatian";
    return "Belum lengkap";
  })();

  // Sticky-footer disabled state — reflects the exact minimum-submit rules
  // (6 stars filled + identity + kebahagiaan + rekomendasi + body ≥ 20 chars + 4 consents).
  const allRatingsFilled = keysForRole(reviewerRole).every(({ key }) => typeof ratings[key] === "number" && ratings[key] > 0);
  const kebahagiaanValid = kebahagiaan !== null;
  const rekomendasiValid = rekomendasi !== null;
  const canSubmit = allRatingsFilled && identityValid && kebahagiaanValid && rekomendasiValid && bodyValid && allConsentsChecked && !busy;

  // Determine the first invalid field in visual (top-down) order — used to auto-scroll on failed submit.
  const firstInvalidRef = () => {
    if (multiLevel && !educationLevelReviewed) return levelRef;
    if (!periodValid) return periodRef;
    const missing = keysForRole(reviewerRole).find(({ key }) => !(ratings[key] > 0));
    if (missing) return { current: ratingRefs.current[missing.key] };
    if (!kebahagiaanValid) return kebahagiaanRef;
    if (!rekomendasiValid) return rekomendasiRef;
    if (!bodyValid) return bodyRef;
    if (!allConsentsChecked) return consentsRef;
    return null;
  };
  const handleSubmitClick = () => {
    const err = firstInvalidRef();
    if (err) {
      setSubmitAttempted(true);
      const el = err.current;
      if (el && typeof el.scrollIntoView === "function") {
        try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* noop */ }
      }
      return;
    }
    submit();
  };

  return (
    <div data-testid="review-wizard" className="bg-white sm:border sm:border-[#EAE7E0] sm:rounded-2xl overflow-hidden">
      {/* Spec v1.1 §2 — single-page scroll-spy indicator (Penilaian · Cerita · Kirim).
          Renders inline (not sticky per screen height) so it doesn't hijack focus
          from the sticky footer. */}
      <div className="px-5 sm:px-6 pt-5 pb-4 border-b border-[#EAE7E0] bg-[#FAFAF8]">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs uppercase tracking-widest text-[#1E3A8A] font-semibold">Tulis Review · {schoolName || "Sekolah"}</div>
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest">
            <a href="#sec-penilaian" className={"pb-0.5 border-b-2 " + (allRatingsFilled ? "text-[#166534] border-[#166534]" : "text-[#1E3A8A] border-[#1E3A8A]")}>Penilaian</a>
            <span className="text-[#EAE7E0]">·</span>
            <a href="#sec-cerita" className={"pb-0.5 border-b-2 " + (bodyValid ? "text-[#166534] border-[#166534]" : "text-[#4B5563] border-transparent")}>Cerita</a>
            <span className="text-[#EAE7E0]">·</span>
            <a href="#sec-kirim" className={"pb-0.5 border-b-2 " + (allConsentsChecked ? "text-[#166534] border-[#166534]" : "text-[#4B5563] border-transparent")}>Kirim</a>
          </div>
        </div>
      </div>

      <div className="p-5 sm:p-6 space-y-5">
        {/* ===== SECTION 1: Identity + 6 Kualitas + Kebahagiaan + Rekomendasi ===== */}
        <div id="sec-penilaian" data-testid="wizard-step-1" className="space-y-4 scroll-mt-24">
          {/* Persona / identity picker card — kept above the numbered Kualitas block
              because it drives WHICH rating rows appear (students skip Value). */}

            {/* Reviewer role picker */}
            <div className="p-4 rounded-2xl bg-[#F3F1ED] border border-[#EAE7E0]">
              <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-2">Anda menulis sebagai</div>
              <div className="grid grid-cols-3 gap-2" data-testid="wizard-role-picker">
                {[
                  { v: "parent", label: "Orang Tua", cls: "text-[#1E3A8A]" },
                  { v: "student", label: "Siswa Aktif", cls: "text-[#065F46]" },
                  { v: "alumni", label: "Alumni", cls: "text-[#7C2D12]" },
                ].map(({ v, label, cls }) => (
                  <button key={v} type="button" onClick={() => setReviewerRole(v)} data-testid={`wizard-role-${v}`}
                    className={"px-3 py-2 rounded-xl text-sm font-medium border transition-all " + (reviewerRole === v
                      ? "bg-white border-[#1E3A8A] " + cls + " shadow-sm"
                      : "bg-white/60 border-[#EAE7E0] text-[#4B5563] hover:border-[#1E3A8A]/40")}>
                    {label}
                  </button>
                ))}
              </div>
              {multiLevel && (
                <div className="mt-3" data-testid="wizard-education-level-block" ref={levelRef}>
                  <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5">
                    Jenjang yang Anda review *
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {uniqueLevels.map((lv) => (
                      <button
                        key={lv}
                        type="button"
                        onClick={() => setEducationLevelReviewed(lv)}
                        data-testid={`wizard-education-level-${lv}`}
                        className={"px-4 py-1.5 rounded-full text-sm font-medium border transition-colors " +
                          (educationLevelReviewed === lv
                            ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                            : "bg-white text-[#4B5563] border-[#EAE7E0] hover:border-[#1E3A8A]/40")}>
                        {lv}
                      </button>
                    ))}
                  </div>
                  <div className="text-[10px] text-[#4B5563] italic mt-1 leading-snug">
                    Sekolah ini menawarkan {uniqueLevels.join(", ")}. Pilih jenjang tempat Anda / anak Anda berpengalaman — review akan bisa disaring per jenjang.
                  </div>
                  {submitAttempted && !levelValid && (
                    <div className="mt-1 text-[11px] text-[#B91C1C] font-medium" data-testid="wizard-error-level">
                      Pilih jenjang yang di-review
                    </div>
                  )}
                </div>
              )}
              {/* Iter140 — Periode bersekolah (unified two-dropdown range picker for every role). */}
              <div className="mt-3" data-testid="wizard-attended-period-block" ref={periodRef}>
                <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5">
                  {reviewerRole === "parent"
                    ? "PERIODE ANAK ANDA BERSEKOLAH DI SINI *"
                    : "PERIODE ANDA BERSEKOLAH DI SINI *"}
                </div>
                <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-end">
                  {/* Tahun masuk */}
                  <div className="min-w-0">
                    <div className="text-[10px] text-[#6B7280] mb-1">Tahun masuk</div>
                    <Select value={yearStart} onValueChange={setYearStart}>
                      <SelectTrigger className="rounded-xl h-10" data-testid="wizard-year-start">
                        <SelectValue placeholder="Pilih…" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72 z-[200]">
                        {(() => {
                          const opts = [];
                          for (let y = currentYear; y >= MIN_YEAR; y--) {
                            opts.push(
                              <SelectItem key={y} value={String(y)}>
                                {y}
                              </SelectItem>
                            );
                          }
                          return opts;
                        })()}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Arrow separator */}
                  <div className="pb-2 text-[#9CA3AF]" aria-hidden="true">
                    <ChevronRight size={18} />
                  </div>
                  {/* Sampai / Tahun lulus */}
                  <div className="min-w-0">
                    <div className="text-[10px] text-[#6B7280] mb-1">
                      {reviewerRole === "alumni" ? "Tahun lulus" : "Sampai"}
                    </div>
                    <Select value={yearEnd} onValueChange={setYearEnd} disabled={!yearStart}>
                      <SelectTrigger className="rounded-xl h-10" data-testid="wizard-year-end">
                        <SelectValue placeholder={yearStart ? "Pilih…" : "Pilih tahun masuk dulu"} />
                      </SelectTrigger>
                      <SelectContent className="max-h-72 z-[200]">
                        {(() => {
                          const opts = [];
                          if (allowCurrentEnd) {
                            opts.push(
                              <SelectItem
                                key="current"
                                value="current"
                                data-testid="wizard-year-end-current"
                              >
                                Sekarang
                              </SelectItem>
                            );
                          }
                          const startNum = yearStart ? parseInt(yearStart, 10) : MIN_YEAR;
                          for (let y = currentYear; y >= MIN_YEAR; y--) {
                            opts.push(
                              <SelectItem
                                key={y}
                                value={String(y)}
                                disabled={y < startNum}
                                data-testid={`wizard-year-end-${y}`}
                              >
                                {y}
                              </SelectItem>
                            );
                          }
                          return opts;
                        })()}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* Preview label publik (real-time) */}
                {periodePreview && (
                  <div
                    className="mt-2 text-[11px] text-[#4B5563] italic leading-snug"
                    data-testid="wizard-period-preview"
                  >
                    <span className="not-italic font-medium text-[#1B2B4D]">{periodePreview}</span>
                  </div>
                )}
                {submitAttempted && !periodValid && (
                  <div className="mt-1 text-[11px] text-[#B91C1C] font-medium" data-testid="wizard-error-period">
                    Periode sekolah belum dipilih
                  </div>
                )}
              </div>
              {reviewerRole === "alumni" && (
                <div className="mt-3 space-y-3" data-testid="wizard-alumni-fields">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5">Jenjang pendidikan tertinggi <span className="italic normal-case tracking-normal">(opsional)</span></div>
                    <Select value={alumniHighestEdu || "__none"} onValueChange={(v) => setAlumniHighestEdu(v === "__none" ? "" : v)}>
                      <SelectTrigger className="rounded-xl h-10" data-testid="wizard-alumni-highest-edu">
                        <SelectValue placeholder="Pilih jenjang…" />
                      </SelectTrigger>
                      <SelectContent className="z-[200]">
                        <SelectItem value="__none">— Belum kuliah / lewati —</SelectItem>
                        <SelectItem value="D3">D3 (Diploma)</SelectItem>
                        <SelectItem value="S1">S1 (Sarjana)</SelectItem>
                        <SelectItem value="S2">S2 (Magister)</SelectItem>
                        <SelectItem value="S3">S3 (Doktor)</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="text-[10px] text-[#4B5563] italic mt-1 leading-snug">Kolom universitas per jenjang akan muncul di bawah — isi jejak akademik lengkap.</div>
                  </div>

                  {/* Universitas per jenjang: tampilkan D3→S3 sampai jenjang tertinggi terpilih */}
                  {(alumniHighestEdu === "D3" || alumniHighestEdu === "S1" || alumniHighestEdu === "S2" || alumniHighestEdu === "S3") && (
                    <div className="space-y-2 pl-3 border-l-2 border-[#DBEAFE]">
                      {(alumniHighestEdu === "D3") && (
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5">Universitas D3</div>
                          <AlumniAutocomplete field="university" value={alumniEduD3} onChange={setAlumniEduD3}
                            placeholder="mis. Politeknik Negeri Jakarta" testid="wizard-alumni-edu-d3" />
                        </div>
                      )}
                      {(alumniHighestEdu === "S1" || alumniHighestEdu === "S2" || alumniHighestEdu === "S3") && (
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5">Universitas S1 (Sarjana)</div>
                          <AlumniAutocomplete field="university" value={alumniEduS1} onChange={setAlumniEduS1}
                            placeholder="mis. Universitas Indonesia" testid="wizard-alumni-edu-s1" />
                        </div>
                      )}
                      {(alumniHighestEdu === "S2" || alumniHighestEdu === "S3") && (
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5">Universitas S2 (Magister)</div>
                          <AlumniAutocomplete field="university" value={alumniEduS2} onChange={setAlumniEduS2}
                            placeholder="mis. National University of Singapore" testid="wizard-alumni-edu-s2" />
                        </div>
                      )}
                      {(alumniHighestEdu === "S3") && (
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5">Universitas S3 (Doktor)</div>
                          <AlumniAutocomplete field="university" value={alumniEduS3} onChange={setAlumniEduS3}
                            placeholder="mis. University of Cambridge" testid="wizard-alumni-edu-s3" />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Fallback / legacy free-text (opsional, tetap ada untuk backward-compat & flexibility) */}
                  {!alumniHighestEdu && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5">Di mana Anda melanjutkan studi setelah lulus? <span className="italic normal-case tracking-normal">(opsional, internal)</span></div>
                      <AlumniAutocomplete field="university" value={alumniNextStudy} onChange={setAlumniNextStudy}
                        placeholder="mis. Universitas Indonesia, MIT, NUS…" testid="wizard-alumni-study" />
                    </div>
                  )}

                  {/* Detail profesi: profesi + perusahaan saat ini + perusahaan sebelumnya */}
                  <div className="pt-2 border-t border-[#EAE7E0]">
                    <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5">Profesi / Jabatan saat ini <span className="italic normal-case tracking-normal">(opsional, internal)</span></div>
                    <Input placeholder="mis. Software Engineer, Dokter Umum, Guru SMA…"
                      value={alumniProfession} onChange={(e) => setAlumniProfession(e.target.value)} data-testid="wizard-alumni-profession"
                      className="rounded-xl h-10" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5">Perusahaan saat ini <span className="italic normal-case tracking-normal">(opsional)</span></div>
                    <AlumniAutocomplete field="company" value={alumniCurrentCompany} onChange={setAlumniCurrentCompany}
                      placeholder="mis. Gojek, PT Bank Mandiri, RS Cipto…" testid="wizard-alumni-current-company" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5">Perusahaan sebelumnya <span className="italic normal-case tracking-normal">(opsional)</span></div>
                    <AlumniAutocomplete field="company" value={alumniPreviousCompany} onChange={setAlumniPreviousCompany}
                      placeholder="mis. Traveloka, PwC, McKinsey…" testid="wizard-alumni-previous-company" />
                  </div>

                  <div className="text-[10px] text-[#4B5563] italic leading-snug">🔒 Data lanjutan studi, profesi &amp; perusahaan disimpan internal untuk analitik alumni — tidak ditampilkan di review publik.</div>
                </div>
              )}
            </div>

            {/* Numbered-badge card ONE — Review Sekolah. Six rating rows share a
                single container so the block reads as one coherent form step, not
                six loose list items. */}
            <div className="p-4 sm:p-5 rounded-2xl bg-white border border-[#EAE7E0] shadow-sm" data-testid="wizard-block-1-kualitas">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-[#D2673E] text-white text-sm font-bold flex items-center justify-center flex-shrink-0" aria-hidden="true">1</div>
                <div className="flex-1 min-w-0">
                  <div className="text-base sm:text-lg font-semibold text-[#1F2937] leading-tight">Review Sekolah</div>
                  <div className="text-xs sm:text-[13px] text-[#4B5563] mt-1 leading-snug">Enam aspek ini membentuk skor sekolah. Ketuk bintang — sisi kiri untuk setengah bintang.</div>
                </div>
              </div>
              <div className="mt-4 divide-y divide-[#EAE7E0]">
                {keysForRole(reviewerRole).map(({ key, label }, idx) => {
                  const helper = getHelper(key, reviewerRole);
                  const filled = typeof ratings[key] === "number" && ratings[key] > 0;
                  return (
                    <div
                      key={key}
                      className={"py-3 " + (idx === 0 ? "pt-1" : "")}
                      data-testid={`wizard-rating-row-${key}`}
                      ref={(el) => { ratingRefs.current[key] = el; }}
                    >
                      {/* Row 1 — label + question. Full width so long questions don't
                          fight the star picker for horizontal space on mobile. */}
                      <div className="min-w-0">
                        <div className="text-sm sm:text-[15px] text-[#1F2937] font-semibold">{label}</div>
                        {helper && <div className="text-[11px] sm:text-xs text-[#4B5563] mt-1 leading-snug italic">{helper}</div>}
                      </div>
                      {/* Row 2 — StarPicker only (it renders its own `x.x / 5.0` value column
                          internally, so we no longer stack a duplicate label next to it). */}
                      <div className="mt-2 flex items-center gap-2 flex-wrap max-w-full">
                        <div className="max-w-full overflow-hidden">
                          <StarPicker value={ratings[key]} onChange={(v) => setRatings((r) => ({ ...r, [key]: v }))} testid={`wizard-rating-${key}`} size={36} />
                        </div>
                      </div>
                      {submitAttempted && !filled && (
                        <div className="mt-1 text-[11px] text-[#B91C1C] font-medium" data-testid={`wizard-error-rating-${key}`}>
                          Beri nilai untuk {label}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="p-4 sm:p-5 rounded-2xl bg-[#FFF8E6] border border-[#F1C97A]" data-testid="wizard-kebahagiaan" ref={kebahagiaanRef}>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-[#B0781F] text-white text-sm font-bold flex items-center justify-center flex-shrink-0" aria-hidden="true">2</div>
                <div className="flex-1 min-w-0">
                  <div className="text-base sm:text-lg font-semibold text-[#8A5A00] leading-tight inline-flex items-center gap-2 flex-wrap">
                    Kebahagiaan Siswa
                    <span className="text-[10px] uppercase tracking-widest bg-white/70 text-[#8A5A00]/80 border border-[#F1C97A] px-2 py-0.5 rounded-full font-medium normal-case">tidak memengaruhi skor</span>
                  </div>
                  <div className="text-xs sm:text-[13px] text-[#4B5563] mt-1 leading-snug" data-testid="wizard-kebahagiaan-question">
                    {reviewerRole === "parent"
                      ? "Secara keseluruhan, apakah anak terlihat senang, nyaman, dan antusias menjalani kesehariannya di sekolah?"
                      : "Secara keseluruhan, apakah Anda senang, nyaman, dan antusias menjalani keseharian di sekolah?"}
                  </div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-4 gap-2">
                {[
                  { v: 1, emoji: "😐", label: "Biasa Saja" },
                  { v: 2, emoji: "🙂", label: "Cukup Senang" },
                  { v: 3, emoji: "😊", label: "Senang" },
                  { v: 4, emoji: "😄", label: "Sangat Senang" },
                ].map(({ v, emoji, label }) => {
                  const active = kebahagiaan === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setKebahagiaan(active ? null : v)}
                      data-testid={`wizard-kebahagiaan-${v}`}
                      title={label}
                      aria-label={label}
                      aria-pressed={active}
                      className={"flex flex-col items-center gap-1 py-2 rounded-xl border transition-all " + (active
                        ? "bg-white border-[#B0781F] shadow-sm ring-2 ring-[#F1C97A]"
                        : "bg-white/60 border-[#EAE7E0] hover:border-[#F1C97A]")}
                    >
                      <span className="text-2xl leading-none" aria-hidden="true">{emoji}</span>
                      <span className="text-[9px] uppercase tracking-widest text-[#4B5563] text-center leading-tight">{label}</span>
                    </button>
                  );
                })}
              </div>
              {submitAttempted && !kebahagiaanValid && (
                <div className="mt-2 text-[11px] text-[#B91C1C] font-medium" data-testid="wizard-error-kebahagiaan">
                  Pilih tingkat kebahagiaan
                </div>
              )}
            </div>
            <div className="p-4 sm:p-5 rounded-2xl bg-[#EFF6FF] border border-[#DBEAFE]" data-testid="wizard-rekomendasi" ref={rekomendasiRef}>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-[#1E3A8A] text-white text-sm font-bold flex items-center justify-center flex-shrink-0" aria-hidden="true">3</div>
                <div className="flex-1 min-w-0">
                  <div className="text-base sm:text-lg font-semibold text-[#1E3A8A] leading-tight inline-flex items-center gap-2 flex-wrap">
                    Rekomendasi
                  </div>
                  <div className="text-xs sm:text-[13px] text-[#4B5563] mt-1 leading-snug" data-testid="wizard-rekomendasi-question">
                    {reviewerRole === "parent"
                      ? "Seberapa besar kemungkinan Anda merekomendasikan sekolah ini ke orang tua lain?"
                      : "Seberapa besar kemungkinan Anda merekomendasikan sekolah ini ke calon siswa lain?"}
                  </div>
                </div>
              </div>
              {/* 11-button number picker (0..10). Optional — value only sent to
                  backend if user picks a number. No default center-anchoring so
                  we don't bias reviewers toward 5. Buttons use `flex-1` so all
                  11 stay in one row on 390px without horizontal scroll (each
                  cell ~25px on the tightest viewport). */}
              <div className="mt-4 flex items-stretch gap-1" role="radiogroup" aria-label="Skor rekomendasi 0 sampai 10">
                {[0,1,2,3,4,5,6,7,8,9,10].map((n) => {
                  const active = rekomendasi === n;
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRekomendasi(active ? null : n)}
                      data-testid={`wizard-rekomendasi-${n}`}
                      role="radio"
                      aria-checked={active}
                      className={
                        "flex-1 min-w-0 h-10 rounded-lg text-sm font-semibold tabular-nums transition-colors border " +
                        (active
                          ? "bg-[#1E3A8A] text-white border-[#1E3A8A] shadow-sm"
                          : "bg-white text-[#1F2937] border-[#DBEAFE] hover:border-[#1E3A8A]/60 hover:bg-[#F5F8FF]")
                      }
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 flex items-center justify-between text-[11px] text-[#4B5563]" data-testid="wizard-rekomendasi-scale-labels">
                <span>0 · Sangat Kecil</span>
                <span>Sangat besar · 10</span>
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs" data-testid="wizard-rekomendasi-status">
                {rekomendasi !== null && (
                  <>
                    <span className="inline-block w-2 h-2 rounded-full bg-[#16A34A]" aria-hidden="true" />
                    <span className="text-[#1F2937]">Nilai Rekomendasi: <span className="font-semibold tabular-nums">{rekomendasi} / 10</span></span>
                    <button
                      type="button"
                      onClick={() => setRekomendasi(null)}
                      data-testid="wizard-rekomendasi-clear"
                      className="ml-auto text-[10px] uppercase tracking-widest text-[#4B5563] hover:text-[#B91C1C]"
                    >
                      Kosongkan
                    </button>
                  </>
                )}
              </div>
              {submitAttempted && !rekomendasiValid && (
                <div className="mt-2 text-[11px] text-[#B91C1C] font-medium" data-testid="wizard-error-rekomendasi">
                  Wajib diisi - Seberapa layak direkomendasi
                </div>
              )}
            </div>
          </div>

        {/* ===== SECTION 2: Cerita (main body + optional pros/cons) ===== */}
        <div id="sec-cerita" data-testid="wizard-step-2" className="scroll-mt-24 pt-2">
            <div className="p-4 sm:p-5 rounded-2xl bg-white border border-[#EAE7E0] shadow-sm">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-[#D2673E] text-white text-sm font-bold flex items-center justify-center flex-shrink-0" aria-hidden="true">4</div>
                <div className="flex-1 min-w-0">
                  <div className="text-base sm:text-lg font-semibold text-[#1F2937] leading-tight">Ceritakan pengalaman</div>
                  <div className="text-xs sm:text-[13px] text-[#4B5563] mt-1 leading-snug">Pengalaman Anda sangat berharga bagi ribuan orang tua yang sedang mencari sekolah.</div>
                </div>
              </div>

              <div className="mt-4" ref={bodyRef}>
              <div className="text-sm font-semibold text-[#1F2937] mb-1.5">Ceritakan pengalamanmu</div>
              <Textarea
                placeholder="Apa yang berkesan, apa yang Anda harap orang lain tahu."
                rows={6}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                data-testid="wizard-body"
                className="rounded-xl border-[#EAE7E0] text-[15px] leading-relaxed"
              />
              <div className="flex items-center justify-between mt-1.5">
                <div className="text-[11px] text-[#4B5563] italic leading-snug">💡 Minimal {MAIN_STORY_MIN} karakter — cukup satu paragraf singkat.</div>
                <div className={"text-[11px] tabular-nums " + (bodyValid ? "text-[#166534]" : "text-[#4B5563]")} data-testid="wizard-body-count">{body.trim().length}/{MAIN_STORY_MIN}+</div>
              </div>
              {submitAttempted && !bodyValid && (
                <div className="mt-1 text-[11px] text-[#B91C1C] font-medium" data-testid="wizard-error-body">
                  Cerita minimal {MAIN_STORY_MIN} karakter
                </div>
              )}
              </div>

              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#166534] mb-2 inline-flex items-center gap-1.5 font-semibold"><ThumbsUp size={12} /> Apa yang sudah baik? <span className="italic normal-case tracking-normal text-[#166534]/70 ml-0.5">(Opsional)</span></div>
                  <Textarea placeholder="contoh: Ekskul beragam, fasilitas lab lengkap…" rows={3} value={pros} onChange={(e) => setPros(e.target.value)} data-testid="wizard-pros" className="rounded-xl border-[#BBF7D0] bg-[#F0FDF4]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#854D0E] mb-2 inline-flex items-center gap-1.5 font-semibold"><ThumbsDown size={12} /> Yang perlu ditingkatkan? <span className="italic normal-case tracking-normal text-[#854D0E]/70 ml-0.5">(Opsional)</span></div>
                  <Textarea placeholder="contoh: Antrian jemputan panjang, uang gedung tinggi…" rows={3} value={cons} onChange={(e) => setCons(e.target.value)} data-testid="wizard-cons" className="rounded-xl border-[#FDE68A] bg-[#FEFCE8]" />
                </div>
              </div>
            </div>
        </div>

        {/* ===== SECTION 3 (block 5): Kirim — Anonim toggle + 4-point Persetujuan ===== */}
        <div id="sec-kirim" data-testid="wizard-step-3" className="scroll-mt-24 pt-2">
            <div className="p-4 sm:p-5 rounded-2xl bg-white border border-[#EAE7E0] shadow-sm">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-[#166534] text-white text-sm font-bold flex items-center justify-center flex-shrink-0" aria-hidden="true">5</div>
                <div className="flex-1 min-w-0">
                  <div className="text-base sm:text-lg font-semibold text-[#1F2937] leading-tight inline-flex items-center gap-2">
                    <Sparkles className="text-[#E07A5F]" size={16} /> Kirim review
                  </div>
                  <div className="text-xs sm:text-[13px] text-[#4B5563] mt-1 leading-snug">Cek sekali lagi, centang persetujuan, lalu klik Kirim di bawah.</div>
                </div>
              </div>

              <div className="mt-4 space-y-4">

            {/* Anonim toggle */}
            <label className="p-3 rounded-xl bg-white border border-[#EAE7E0] flex items-start gap-3 cursor-pointer hover:border-[#1E3A8A]/40 transition-colors" data-testid="wizard-anonymous-label">
              <input
                type="checkbox"
                checked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
                data-testid="wizard-anonymous-toggle"
                className="mt-0.5 w-4 h-4 accent-[#1E3A8A]"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold text-[#1F2937]">Kirim sebagai Anonim</div>
                <div className="text-[11px] text-[#4B5563] mt-0.5 leading-snug">
                  {isAnonymous
                    ? <>Review akan tampil dengan nama <span className="font-semibold">Anonim</span>. Tim editorial tetap tahu identitas Anda untuk verifikasi.</>
                    : <>Nama Anda akan disamarkan otomatis (contoh: <em>Budi Suwito</em> tampil sebagai <span className="font-mono text-[#1E3A8A]">Bud* ******</span>). Centang untuk menyembunyikan seluruhnya.</>}
                </div>
              </div>
            </label>

            {/* Spec v1.1 §2 — Persetujuan 4 poin, semuanya wajib */}
            <div className="p-4 rounded-xl bg-[#EFF6FF] border border-[#DBEAFE]" data-testid="wizard-consent-block" ref={consentsRef}>
              <div className="text-sm font-semibold text-[#1F2937] mb-2">Dengan mengirim review ini, saya menyatakan bahwa:</div>
              <div className="space-y-2 text-[13px] text-[#374151]">
                {[
                  { s: consent1, setter: setConsent1, id: "consent-1", text: <>Review ini berasal dari <b>pengalaman pribadi saya</b>.</> },
                  { s: consent2, setter: setConsent2, id: "consent-2", text: <>Saya <b>tidak menerima imbalan atau tekanan</b> untuk menulis review ini.</> },
                  { s: consent3, setter: setConsent3, id: "consent-3", text: <>Saya <b>tidak mencantumkan identitas atau informasi sensitif anak</b>.</> },
                  { s: consent4, setter: setConsent4, id: "consent-4", text: <>Saya menyetujui{" "}<button type="button" onClick={(e) => { e.preventDefault(); setGuidelinesOpen(true); }} data-testid="wizard-guidelines-link" className="text-[#1E3A8A] hover:text-[#E07A5F] font-semibold underline underline-offset-2">Pedoman Komunitas &amp; Review</button>.</> },
                ].map(({ s, setter, id, text }) => (
                  <label key={id} data-testid={`wizard-${id}-label`} className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={s}
                      onChange={(e) => setter(e.target.checked)}
                      data-testid={`wizard-${id}`}
                      className="mt-0.5 w-4 h-4 accent-[#1E3A8A] flex-shrink-0"
                    />
                    <span className="leading-snug">{text}</span>
                  </label>
                ))}
              </div>
              {submitAttempted && !allConsentsChecked && (
                <div className="mt-2 text-[11px] text-[#B91C1C] font-medium" data-testid="wizard-error-consents">
                  Centang semua persetujuan untuk mengirim
                </div>
              )}
            </div>
              </div>
            </div>
        </div>
      </div>

      {/* Community Guidelines overlay — inline plain overlay (was a nested
          Radix Dialog which caused a freeze race). Conditional render →
          guaranteed unmount → no portal → no body side-effects → no race. */}
      {guidelinesOpen && (
        <div
          data-testid="guidelines-overlay"
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Pedoman Komunitas dan Review"
        >
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setGuidelinesOpen(false)}
            data-testid="guidelines-backdrop"
            aria-hidden="true"
          />
          <div
            className="relative bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl p-6"
            data-testid="guidelines-modal"
          >
            <button
              type="button"
              onClick={() => setGuidelinesOpen(false)}
              data-testid="guidelines-x-btn"
              aria-label="Tutup Pedoman"
              className="absolute right-3 top-3 w-8 h-8 rounded-full text-[#4B5563] hover:bg-[#F3F1ED] flex items-center justify-center text-xl"
            >
              ×
            </button>
            <div className="font-serif-display text-2xl text-[#1F2937] pr-8">Pedoman Komunitas dan Review</div>
            <div className="text-xs text-[#4B5563]">The School Reports · Bahasa Indonesia</div>
            <div className="text-sm text-[#374151] leading-relaxed space-y-4 mt-4">
              <p>Terima kasih telah berkontribusi. Panduan berikut memastikan setiap review bermanfaat untuk orang tua, siswa, dan alumni di seluruh Jabodetabek.</p>
              <div><div className="font-serif-display text-lg text-[#1F2937]">1. Review berasal dari pengalaman pribadi</div><p className="mt-1">Anda hanya boleh mengulas sekolah yang benar-benar Anda kenal langsung sebagai orang tua wali murid, siswa aktif, atau alumni. Jangan menulis berdasarkan cerita pihak ketiga.</p></div>
              <div><div className="font-serif-display text-lg text-[#1F2937]">2. Independensi</div><p className="mt-1">Review tidak boleh ditulis atas imbalan (uang, diskon, hadiah) atau di bawah tekanan dari sekolah, agen, atau pihak lain.</p></div>
              <div><div className="font-serif-display text-lg text-[#1F2937]">3. Privasi anak</div><p className="mt-1">Jangan mencantumkan nama lengkap, foto wajah, NISN, alamat rumah, nomor kontak, atau informasi sensitif anak/keluarga lain.</p></div>
              <div><div className="font-serif-display text-lg text-[#1F2937]">4. Masukan yang membangun</div><p className="mt-1">Bahasa sopan, tanpa serangan pribadi terhadap guru/staf. Kritik boleh tajam namun harus faktual, spesifik dan konstruktif.</p></div>
            </div>
            <div className="flex justify-end mt-4">
              <Button onClick={() => setGuidelinesOpen(false)} data-testid="guidelines-close-btn" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-10">Saya mengerti</Button>
            </div>
          </div>
        </div>
      )}

      {/* Spec v1.1 §2 — sticky footer with live headline score + primary Kirim CTA. */}
      <div className="sticky bottom-0 px-5 sm:px-6 py-3 border-t border-[#EAE7E0] bg-white/95 backdrop-blur flex items-center justify-between gap-3 flex-wrap shadow-[0_-4px_18px_-8px_rgba(31,41,55,0.12)]" data-testid="wizard-sticky-footer">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex flex-col">
            <div className="text-[10px] uppercase tracking-widest text-[#4B5563]">Skor Hidup</div>
            <div className="flex items-center gap-2">
              <StarRating value={avgOverall} size={16} showHalf useBandColor />
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border font-semibold whitespace-nowrap"
                style={{ backgroundColor: scoreBand(avgOverall).bg, borderColor: scoreBand(avgOverall).border, color: scoreBand(avgOverall).text }}
                data-testid="wizard-live-score-pill"
              >
                {avgOverall > 0 ? `${avgOverall.toFixed(1)} · ${scoreLabelText}` : scoreLabelText}
              </span>
            </div>
            {draftSavedAt && (
              <div className="text-[10px] text-[#8A8F99] mt-0.5 leading-none" data-testid="wizard-draft-status">
                💾 Draft tersimpan · {draftAgeLabel}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {submitStage > 0 && (
            <div
              data-testid="wizard-submit-status"
              className={"text-xs font-medium " + (submitStage === 2 ? "text-[#B45309]" : "text-[#4B5563]")}
              role="status"
              aria-live="polite"
            >
              {submitStage === 2
                ? "Masih memproses review Anda. Mohon jangan refresh halaman ini."
                : "Sedang mengirim review Anda…"}
            </div>
          )}
          <Button variant="outline" onClick={onCancel} data-testid="wizard-cancel-btn" disabled={busy} className="rounded-full border-[#EAE7E0] h-10 disabled:opacity-50 disabled:cursor-not-allowed">Batal</Button>
          <Button
            onClick={handleSubmitClick}
            disabled={busy}
            data-testid="wizard-submit-btn"
            aria-busy={busy}
            className="rounded-full bg-[#E07A5F] hover:bg-[#C96D55] text-white h-10 px-5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? (
              <>
                <span
                  aria-hidden="true"
                  className="inline-block w-3.5 h-3.5 mr-1.5 rounded-full border-2 border-white/60 border-t-white animate-spin"
                />
                Mengirim…
              </>
            ) : (<><Send size={16} className="mr-1.5" /> Submit</>)}
          </Button>
        </div>
      </div>

      {/* Iter154 — Session expired modal */}
      {sessionExpired && (
        <div
          data-testid="wizard-session-expired"
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
        >
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 relative">
            <button
              type="button"
              onClick={() => setSessionExpired(false)}
              data-testid="wizard-session-expired-close"
              className="absolute top-3 right-3 text-[#4B5563] hover:text-[#1F2937] p-1 rounded-full hover:bg-[#F3F1ED]"
              aria-label="Tutup"
            >
              <X size={18} />
            </button>
            <h2 className="font-serif-display text-2xl text-[#1F2937] leading-tight">
              Sesi Anda berakhir
            </h2>
            <p className="mt-3 text-[15px] text-[#4B5563] leading-relaxed">
              Masuk kembali untuk mengirim review — data yang sudah Anda isi tidak akan hilang.
            </p>
            <Button
              onClick={() => { _saveReviewReturnTo(); nav("/login"); }}
              data-testid="wizard-session-expired-cta"
              className="mt-6 w-full rounded-full bg-[#1B2B4D] hover:bg-[#152441] text-white h-11"
            >
              Masuk / Daftar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
